import _ from 'lodash'
import { useEffect, useRef } from 'react'
import { Becy } from '@/components/ui/icon'
import { HorizontalCarouselNav } from '@/components/casino/lobby/carousel/HorizontalCarouselNav'
import { OriginalGameCard } from '@/components/original/OriginalGameCard'
import { OriginalGameCardSkeleton } from '@/components/original/OriginalGameCardSkeleton'
import { ORIGINAL_PLAY_GAMES_PER_CAROUSEL, ORIGINAL_SKELETON_COUNT } from '@/config/original'
import { useHorizontalCarousel } from '@/hooks/use-horizontal-carousel'
import type { OriginalGame } from '@/types/original'

interface OriginalGamesCarouselSectionProps {
	games: OriginalGame[]
	currentGameId?: string
	isLoading?: boolean
	isError?: boolean
	emptyMessage?: string
}

export function OriginalGamesCarouselSection({
	games,
	currentGameId,
	isLoading = false,
	isError = false,
	emptyMessage = 'No original games available right now.',
}: OriginalGamesCarouselSectionProps) {
	const carouselGames = currentGameId ? _.reject(games, { id: currentGameId }) : games
	const visibleGames = _.take(carouselGames, ORIGINAL_PLAY_GAMES_PER_CAROUSEL)
	const hasCarousel = !isLoading && !isError && visibleGames.length > 0
	const { scrollRef, canScrollPrev, canScrollNext, handleScroll } = useHorizontalCarousel(visibleGames)
	const previousGamesLengthRef = useRef(visibleGames.length)

	useEffect(() => {
		if (visibleGames.length > previousGamesLengthRef.current && scrollRef.current) {
			scrollRef.current.scrollTo({
				left: scrollRef.current.scrollWidth,
				behavior: 'auto',
			})
		}

		previousGamesLengthRef.current = visibleGames.length
	}, [scrollRef, visibleGames.length])

	return (
		<section className='flex flex-col gap-4'>
			<div className='flex items-center justify-between gap-4'>
				<div className='flex items-center gap-3'>
					<Becy className='h-7 w-7 text-primary' />
					<h2 className='text-2xl font-semibold tracking-[-0.12px] text-foreground'>BetsCycle Original Games</h2>
				</div>
				{hasCarousel ? <HorizontalCarouselNav canScrollPrev={canScrollPrev} canScrollNext={canScrollNext} onScroll={handleScroll} /> : null}
			</div>

			{isLoading ? (
				<div className='flex gap-6 overflow-x-auto pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
					{_.times(ORIGINAL_SKELETON_COUNT, (index) => (
						<OriginalGameCardSkeleton key={`original-play-skeleton-${index}`} className='w-[150px] shrink-0' />
					))}
				</div>
			) : null}

			{isError ? <p className='text-sm text-muted-foreground'>Unable to load original games right now.</p> : null}

			{!isLoading && !isError && visibleGames.length === 0 ? <p className='text-sm text-muted-foreground'>{emptyMessage}</p> : null}

			{hasCarousel ? (
				<div className='min-w-0'>
					<div
						ref={scrollRef}
						className='flex gap-6 overflow-x-auto overflow-y-visible scroll-smooth px-[5px] pb-8 pt-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
					>
						{_.map(visibleGames, (game) => (
							<OriginalGameCard key={game.id} game={game} className='w-[150px] shrink-0' />
						))}
					</div>
				</div>
			) : null}
		</section>
	)
}
